// @ts-nocheck
import { BaseOrganizationService } from '@/modules/core/BaseOrganizationService';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { TournamentEngine } from '../core/TournamentEngine';
import type { Team, BracketType } from '../core/TournamentEngine';

type Tournament = Database['public']['Tables']['tournaments']['Row'];
type TournamentInsert = Database['public']['Tables']['tournaments']['Insert'];
type TournamentUpdate = Database['public']['Tables']['tournaments']['Update'];

type Match = Database['public']['Tables']['tournament_matches']['Row'];
type MatchInsert = Database['public']['Tables']['tournament_matches']['Insert'];
type MatchUpdate = Database['public']['Tables']['tournament_matches']['Update'];


export class TournamentService extends BaseOrganizationService<Tournament> {
  constructor(supabase: SupabaseClient, organizationId: string) {
    super(supabase, organizationId, 'tournaments');
  }

  // ============================================
  // TOURNAMENT OPERATIONS (inherit from base)
  // ============================================
  // getAll(), getById(), create(), update(), delete(), count() 
  // are automatically inherited from BaseOrganizationService

  /**
   * Get tournament with all related data (matches, teams)
   */
  async getWithDetails(id: string) {
    const { data, error } = await this.supabase
      .from('tournaments')
      .select(`
        *,
        matches:tournament_matches(*),
        teams:tournament_teams(
          *,
          team:teams(*)
        )
      `)
      .eq('id', id)
      .eq('organization_id', this.organizationId)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get tournaments by status
   */
  async getByStatus(status: 'setup' | 'active' | 'completed' | 'cancelled'): Promise<Tournament[]> {
    const { data, error } = await this.supabase
      .from('tournaments')
      .select('*')
      .eq('organization_id', this.organizationId)
      .eq('status', status)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get tournaments by event
   */
  async getByEvent(eventId: string): Promise<Tournament[]> {
    const { data, error } = await this.supabase
      .from('tournaments')
      .select('*')
      .eq('organization_id', this.organizationId)
      .eq('event_id', eventId)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ============================================
  // POOL OPERATIONS
  // ============================================

  /**
   * Create pools for a tournament and return the created pool records
   */
  async createPools(tournamentId: string, pools: { name: string; pool_order: number }[]): Promise<any[]> {
    const { data, error } = await this.supabase
      .from('tournament_pools')
      .insert(pools.map(p => ({ tournament_id: tournamentId, ...p })))
      .select();
    if (error) throw error;
    return data;
  }

  /**
   * Assign teams to pools
   */
  async assignTeamsToPools(assignments: { pool_id: string; team_id: string }[]): Promise<void> {
    const { error } = await this.supabase
      .from('tournament_pool_teams')
      .insert(assignments);
    if (error) throw error;
  }

  /**
   * Get pools for a tournament with their teams (joined from tournament_brackets)
   */
  async getPools(tournamentId: string): Promise<any[]> {
    const { data, error } = await this.supabase
      .from('tournament_pools')
      .select(`
        id, name, pool_order,
        tournament_pool_teams (
          team_id,
          tournament_brackets:team_id (id, team_name, seed_number, coach_name)
        )
      `)
      .eq('tournament_id', tournamentId)
      .order('pool_order');
    if (error) throw error;
    return data || [];
  }

  /**
   * Delete existing pools for a tournament (cascade deletes pool_teams too)
   */
  async deletePools(tournamentId: string): Promise<void> {
    const { error } = await this.supabase
      .from('tournament_pools')
      .delete()
      .eq('tournament_id', tournamentId);
    if (error) throw error;
  }

  // ============================================
  // MATCH OPERATIONS
  // ============================================

  /**
   * Create match
   */
  async createMatch(match: MatchInsert): Promise<Match> {
    // Verify tournament belongs to org
    const tournament = await this.getById(match.tournament_id);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    const { data, error } = await this.supabase
      .from('tournament_matches')
      .insert(match)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update match score
   */
  async updateMatchScore(
    matchId: string,
    team1Score: number,
    team2Score: number
  ): Promise<Match> {
    const { data, error } = await this.supabase
      .from('tournament_matches')
      .update({
        team1_score: team1Score,
        team2_score: team2Score,
        status: 'completed',
      })
      .eq('id', matchId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get all matches for tournament
   */
  async getMatches(tournamentId: string): Promise<Match[]> {
    // Verify tournament belongs to org
    const tournament = await this.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    const { data, error } = await this.supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('round', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get matches for specific pool
   */
  async getPoolMatches(poolId: string): Promise<Match[]> {
    const { data, error } = await this.supabase
      .from('tournament_matches')
      .select('*')
      .eq('pool_id', poolId)
      .order('match_number', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get matches for specific round
   */
  async getRoundMatches(tournamentId: string, round: number): Promise<Match[]> {
    // Verify tournament belongs to org
    const tournament = await this.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    const { data, error } = await this.supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .eq('round_number', round) // Use round_number from schema
      .order('match_number', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // ============================================
  // STANDINGS & STATISTICS
  // ============================================

  /**
   * Get pool standings (wins/losses/points)
   * NOTE: Placeholder - implement when pools are fully supported
   */
  async getPoolStandings(poolId: string) {
    throw new Error('Pool standings not yet implemented - tournament_pools table required');
  }

  /**
   * Get bracket standings (for elimination tournaments)
   */
  async getBracketStandings(tournamentId: string) {
    // Verify tournament belongs to org
    const tournament = await this.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    const { data, error } = await this.supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .eq('status', 'completed')
      .order('round', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // ============================================
  // TOURNAMENT ENGINE INTEGRATION
  // ============================================

  /**
   * Generate bracket for tournament using TournamentEngine
   * Creates all matches and saves to database
   */
  async generateBracket(
    tournamentId: string,
    teams: Team[],
    format: BracketType
  ) {
    // Validate tournament belongs to org
    const tournament = await this.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    // Validate with TournamentEngine
    const validation = TournamentEngine.validateTournament(teams, format);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Generate matches using pure logic
    const matches = format === 'single_elimination'
      ? TournamentEngine.generateSingleEliminationBracket(teams)
      : TournamentEngine.generateDoubleEliminationBracket(teams);

    console.log(`🎯 Generated ${matches.length} matches for ${format} bracket`);

    // Save to database
    const matchInserts = matches.map(m => ({
      tournament_id: tournamentId,
      round_number: m.round, // Map to round_number in schema
      match_number: m.match_number,
      team1_id: m.team1_id,
      team2_id: m.team2_id,
      team1_score: m.team1_score,
      team2_score: m.team2_score,
      status: m.status,
      winner_id: m.winner_id,
    }));

    const { error } = await this.supabase
      .from('tournament_matches')
      .insert(matchInserts);

    if (error) throw error;

    return matches;
  }

  /**
   * Generate pool play using TournamentEngine
   * Creates pools and round robin schedules
   * NOTE: Requires tournament_pools table
   */
  async generatePoolPlay(tournamentId: string, teams: Team[], numPools: number) {
    // Validate tournament belongs to org
    const tournament = await this.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found or unauthorized');
    }

    // Distribute teams using TournamentEngine
    const pools = TournamentEngine.distributeToPools(teams, numPools);

    console.log(`🎯 Created ${pools.length} pools with ${teams.length} teams`);

    // Create pools in database
    const createdPools = await this.createPools(
      tournamentId,
      pools.map((p, i) => ({ name: p.name, pool_order: i }))
    );

    // Assign teams and generate matches for each pool
    for (let i = 0; i < createdPools.length; i++) {
      const createdPool = createdPools[i];
      const pool = pools[i];

      // Assign teams to pool
      const assignments = pool.teams.map((t: Team) => ({
        pool_id: createdPool.id,
        team_id: t.id,
      }));
      await this.assignTeamsToPools(assignments);

      // Generate round robin matches using TournamentEngine
      const matches = TournamentEngine.generateRoundRobin({
        ...pool,
        id: createdPool.id,
      });

      console.log(`🎯 Generated ${matches.length} matches for ${pool.name}`);

      // Save matches
      const matchInserts = matches.map(m => ({
        tournament_id: tournamentId,
        round_number: m.round,
        match_number: m.match_number,
        team1_id: m.team1_id,
        team2_id: m.team2_id,
        status: m.status,
      }));

      await this.supabase
        .from('tournament_matches')
        .insert(matchInserts);
    }

    return pools;
  }

  /**
   * Advance winner to next match after match completion
   * Uses TournamentEngine logic
   */
  async advanceWinner(matchId: string, winnerId: string) {
    // Get all matches for this tournament
    const match = await this.supabase
      .from('tournament_matches')
      .select('tournament_id')
      .eq('id', matchId)
      .single();

    if (match.error || !match.data) {
      throw new Error('Match not found');
    }

    const allMatches = await this.getMatches(match.data.tournament_id);

    // Use TournamentEngine to calculate advancement
    const updatedMatches = TournamentEngine.advanceWinner(
      allMatches.map(m => ({
        id: m.id,
        round: m.round_number, // Use round_number from schema
        match_number: m.match_number,
        team1_id: m.team1_id,
        team2_id: m.team2_id,
        team1_score: m.team1_score,
        team2_score: m.team2_score,
        winner_id: m.winner_id,
        next_match_id: null, // Not stored in DB
        status: m.status as 'pending' | 'in_progress' | 'completed',
        pool_id: null, // Pool feature not yet implemented
      })),
      matchId,
      winnerId
    );

    // Find the next match that was updated
    const nextMatch = updatedMatches.find(
      m => m.team1_id === winnerId || m.team2_id === winnerId
    );

    if (nextMatch) {
      // Update next match with winner
      const { error } = await this.supabase
        .from('tournament_matches')
        .update({
          team1_id: nextMatch.team1_id,
          team2_id: nextMatch.team2_id,
          status: nextMatch.status,
        })
        .eq('id', nextMatch.id);

      if (error) throw error;

      console.log(`✅ Advanced winner ${winnerId} to match ${nextMatch.id}`);
    }

    return updatedMatches;
  }
}
